import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare,
  Plus, 
  User,
  Settings,
  ChevronLeft,
  LogOut,
  Grid3x3,
  Sparkles,
  Target,
  Search,
  Folder,
  Home
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSwirlModal } from '../../contexts/SwirlModalContext';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { useAuth } from '../../contexts/AuthContext';
import { FLAGS } from '../../lib/flags';
import FoldersList from '../chat/FoldersList';
import ChatHistoryList from '../chat/ChatHistoryList';

export default function Sidebar({ 
  isExpanded, 
  onToggle,
  chats = [],
  currentChatId,
  onChatSelect,
  onPinChat,
  onRenameChat,
  onMoveToProject,
  onArchiveChat,
  onDeleteChat,
  projects = [],
  expandedFolders = new Set(),
  onToggleFolder,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
  onSelectFolder,
  onCreateNewChat,
  pinnedMoments = [],
  onMomentSelect,
  showSearchModal,
  setShowSearchModal
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { openSwirlModal } = useSwirlModal();
  const { openSettingsModal } = useSettingsModal();
  const { signOut, user, userProfile } = useAuth();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showGradient, setShowGradient] = useState(false);
  const dropdownRef = useRef(null);
  const scrollContainerRef = useRef(null);
  
  // Get user's display name
  const userName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Detect scroll to show gradient overlay
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Show gradient if content is scrolled (not at top)
      setShowGradient(scrollTop > 0 && scrollHeight > clientHeight);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    // Check initial state
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [isExpanded, chats]);
  
  const menuItems = [
    ...(FLAGS.SELF_DASHBOARD_ENABLED ? [
      { icon: Home, label: 'Home', path: '/home' },
    ] : []),
    ...(FLAGS.SWIRLSPACE_ENABLED ? [{ icon: Grid3x3, label: 'Swirlspace', path: '/swirlspace' }] : []),
    { icon: Target, label: 'Focus', path: '/focus' },
    { icon: MessageSquare, label: 'Ask Swirl', path: '/ai-chat' },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleSwirlClick = () => {
    // For now, just open the modal without a callback
    // Each page can register its own callback if needed
    openSwirlModal();
  };

  const handleLogout = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        console.error('Logout error:', error);
        alert('Failed to logout. Please try again.');
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to logout. Please try again.');
    }
  };

  return (
    <aside 
      className={`fixed left-0 top-0 h-full bg-white/40 backdrop-blur-lg border-r border-white/50 z-50 flex flex-col transition-all duration-500 ease-out ${
        isExpanded ? 'w-64' : 'w-16'
      }`}
    >
      {/* Header - only visible when expanded */}
      <div className={`px-4 pt-4 mb-6 flex-shrink-0 transition-all duration-500 ease-out ${isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none absolute'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <img 
                src="/logo.svg"
                alt="Swirl Logo"
                className="w-full h-full"
              />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800">Swirl</h1>
              <p className="text-xs text-gray-600">Personal Intelligence</p>
            </div>
          </div>
          <button
            onClick={onToggle}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-100/80 hover:text-gray-800 transition-all duration-200"
          >
            <ChevronLeft size={14} />
          </button>
        </div>
      </div>

      {/* Logo - only visible when minimized */}
      <div className={`pt-4 mb-4 flex-shrink-0 transition-all duration-500 ease-out ${!isExpanded ? 'opacity-100 flex justify-center' : 'opacity-0 pointer-events-none absolute'}`}>
        <div className="w-10 h-10 flex items-center justify-center">
          <img 
            src="/logo.svg"
            alt="Swirl Logo"
            className="w-full h-full"
          />
        </div>
      </div>

      {/* Menu Items */}
      <div className={`flex-1 min-h-0 transition-all duration-500 ease-out ${isExpanded ? 'px-4' : 'px-0'}`}>
        <nav className={`flex-shrink-0 ${isExpanded ? 'space-y-1' : 'space-y-2 flex flex-col items-center'}`}>
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={index}
                onClick={() => handleMenuClick(item.path)}
                className={`transition-all duration-200 ${
                  isExpanded 
                    ? `w-full flex items-center space-x-2.5 px-3 py-2 rounded-xl text-sm ${
                        isActive 
                          ? 'bg-gray-200/80 text-gray-800 font-medium' 
                          : 'text-gray-600 hover:bg-gray-100/80 hover:text-gray-800'
                      }`
                    : `w-10 h-10 rounded-xl flex items-center justify-center ${
                        isActive 
                          ? 'bg-gray-200/80 text-gray-800' 
                          : 'text-gray-600 hover:bg-gray-100/80 hover:text-gray-800'
                      }`
                }`}
                title={!isExpanded ? item.label : undefined}
              >
                <Icon size={18} />
                {isExpanded && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Conditional: Show only on Ask Swirl page */}
        {location.pathname.startsWith('/ai-chat') && (
          <>
            {isExpanded ? (
              <>
                {/* Fixed New Chat and Search Buttons */}
                <div className="mt-3 border-t border-gray-200/50 pt-3 flex-shrink-0">
                  {/* New Chat Button */}
                  <div className="mb-1">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onCreateNewChat) {
                          onCreateNewChat();
                        }
                      }}
                      className="w-full flex items-center space-x-2.5 px-3 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-100/80 hover:text-gray-800 transition-all duration-200"
                    >
                      <Plus size={18} />
                      <span>New chat</span>
                    </button>
                  </div>

                  {/* Search Button - Opens Modal */}
                  <div className="mb-4">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowSearchModal(true);
                      }}
                      className="w-full flex items-center space-x-2.5 px-3 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-100/80 hover:text-gray-800 transition-all duration-200"
                    >
                      <Search size={18} />
                      <span>Search chats</span>
                    </button>
                  </div>
                </div>

                {/* Scrollable Folders and Chats */}
                <div 
                  ref={scrollContainerRef}
                  className="overflow-y-auto flex-1 min-h-0 relative scrollbar-hide"
                  style={{ maxHeight: 'calc(100vh - 380px)' }}
                >
                  <div className="px-4 pb-24">
                    <FoldersList 
                      projects={projects}
                      chats={chats}
                      expandedFolders={expandedFolders}
                      onToggleFolder={onToggleFolder}
                      onCreateProject={onCreateProject}
                      onRenameProject={onRenameProject}
                      onDeleteProject={onDeleteProject}
                      onSelectFolder={onSelectFolder}
                      onChatSelect={onChatSelect}
                      currentChatId={currentChatId}
                    />
                    
                    {/* Chats Label */}
                    <div className="mb-3 mt-4">
                      <h3 className="text-sm text-gray-600">Chats</h3>
                    </div>
                    
                    <ChatHistoryList 
                      chats={chats}
                      currentChatId={currentChatId}
                      onChatSelect={onChatSelect}
                      onPinChat={onPinChat}
                      onRenameChat={onRenameChat}
                      onMoveToProject={onMoveToProject}
                      onArchiveChat={onArchiveChat}
                      onDeleteChat={onDeleteChat}
                      projects={projects}
                      pinnedMoments={pinnedMoments}
                      onMomentSelect={onMomentSelect}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="mt-2 pt-2 border-t border-gray-200/50 flex flex-col items-center space-y-2">
                {/* New Chat Button - Minimized */}
                <button
                  onClick={onCreateNewChat}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-100/80 hover:text-gray-800 transition-all duration-200"
                  title="New chat"
                >
                  <Plus size={18} />
                </button>

                {/* Search Button - Minimized */}
                <button
                  onClick={() => setShowSearchModal(true)}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-100/80 hover:text-gray-800 transition-all duration-200"
                  title="Search chats"
                >
                  <Search size={18} />
                </button>

                {/* Folder Icon */}
                <button
                  onClick={() => {
                    onToggle();
                    // Small delay to ensure sidebar expands before selecting folder
                    setTimeout(() => {
                      if (onSelectFolder) {
                        onSelectFolder(null);
                      }
                    }, 100);
                  }}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-100/80 hover:text-gray-800 transition-all duration-200"
                  title="Folders"
                >
                  <Folder size={18} />
                </button>
                
                {/* Chat History Icon */}
                <button
                  onClick={() => {
                    onToggle();
                    // Small delay to ensure sidebar expands
                    setTimeout(() => {
                      // The ChatHistoryList will be visible once sidebar expands
                    }, 100);
                  }}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-100/80 hover:text-gray-800 transition-all duration-200"
                  title="Recent Chats"
                >
                  <MessageSquare size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky Footer - Swirl It Button and User Profile together */}
      <div className={`flex-shrink-0 z-20 bg-white relative ${isExpanded ? 'px-4 pb-4 pt-4' : 'px-0 pb-4 pt-4'}`}>
        {/* Gradient overlay - only visible when content scrolls behind footer */}
        {isExpanded && showGradient && (
          <div className="absolute bottom-full left-0 right-0 h-4 bg-gradient-to-t from-white via-white/70 to-transparent pointer-events-none" />
        )}
        
        {/* Swirl It Button */}
        <div className={`mb-3 ${isExpanded ? '' : 'flex justify-center'}`}>
          <button 
            onClick={handleSwirlClick}
            className={`bg-gradient-to-br from-pink-400 via-purple-400 to-blue-400 text-gray-800 rounded-full flex items-center justify-center hover:from-pink-500 hover:via-purple-500 hover:to-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl ${
              isExpanded 
                ? 'w-full py-2.5 px-4 gap-1.5 text-sm font-semibold' 
                : 'w-10 h-10'
            }`}
          >
            <Plus size={18} className="flex-shrink-0 -ml-1" />
            {isExpanded && <span className="whitespace-nowrap -ml-0.5">Swirl It</span>}
          </button>
        </div>

        {/* User Profile */}
        <div className="relative" ref={dropdownRef}>
          <button 
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className={`w-full flex items-center transition-all duration-200 text-sm ${
              isExpanded 
                ? 'space-x-3 px-3 py-2.5 rounded-xl hover:bg-gray-50' 
                : 'w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-50 mx-auto'
            }`}
          >
            {isExpanded ? (
              <>
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-white" />
                </div>
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <span className="font-semibold text-gray-900 truncate w-full">{userName}</span>
                  {user?.email && (
                    <span className="text-xs text-gray-500 truncate w-full">{user.email}</span>
                  )}
                </div>
              </>
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
            )}
          </button>
          
          {/* Click-based dropdown - much more reliable */}
          {isExpanded && showUserDropdown && (
            <div className="absolute bottom-full left-0 mb-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                {user?.email || 'User'}
              </div>
              <button
                onClick={() => {
                  setShowUserDropdown(false);
                  navigate('/settings');
                }}
                className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Settings size={16} />
                <span>Settings</span>
              </button>
              <button
                onClick={() => {
                  setShowUserDropdown(false);
                  handleLogout();
                }}
                className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}